/**
 * Mounts the processor's card form (through the adapter registry) into a
 * container and exposes tokenize(). Pages only see this hook.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { PaymentClientConfig } from '../../api/types';
import { PaymentFormAdapter, PaymentFormError, TokenizeDetails } from './adapters/paymentFormAdapter';
import { createPaymentFormAdapter } from './adapters/registry';

export type PaymentFormStatus = 'idle' | 'loading' | 'ready' | 'error';

export function usePaymentForm(clientConfig: PaymentClientConfig | null) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const adapterRef = useRef<PaymentFormAdapter | null>(null);
  const [status, setStatus] = useState<PaymentFormStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const configKey = clientConfig ? JSON.stringify(clientConfig) : '';

  useEffect(() => {
    if (!clientConfig) {
      setStatus('idle');
      return undefined;
    }
    const adapter = createPaymentFormAdapter(clientConfig.provider);
    if (!adapter) {
      setStatus('error');
      setError('Card payments are not available here yet.');
      return undefined;
    }
    let active = true;
    adapterRef.current = adapter;
    setStatus('loading');
    setError(null);
    (async () => {
      try {
        await adapter.load(clientConfig);
        if (!active || !containerRef.current) return;
        await adapter.render(containerRef.current);
        if (active) setStatus('ready');
      } catch (cause) {
        if (!active) return;
        setStatus('error');
        setError(cause instanceof PaymentFormError ? cause.message : 'The card form could not be loaded.');
      }
    })();
    return () => {
      active = false;
      adapter.destroy();
      if (adapterRef.current === adapter) adapterRef.current = null;
    };
    // configKey stands for clientConfig
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey]);

  const tokenize = useCallback(async (details?: TokenizeDetails) => {
    const adapter = adapterRef.current;
    if (!adapter) throw new PaymentFormError('The card form is not ready.');
    return adapter.tokenize(details);
  }, []);

  return { containerRef, status, error, tokenize };
}
